<template>
  <FileReport />
  <div class="student-page-wrapper">
    <!-- Header - Use StudentHeader for students -->
    <StudentHeader />

    <!-- Main Content -->
    <div
      class="max-w-7xl lg:w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 pb-6 sm:pb-8 pt-32 sm:pt-36 md:pt-44"
    >
      <!-- Student Status Banner -->
      <StudentStatusBanner :dismissible="true" />

      <!-- Document Resubmission Alert -->
      <DocumentResubmissionAlert />

      <!-- Page Title -->
      <div class="mb-6 sm:mb-8">
        <div class="flex items-center gap-3 sm:gap-4 md:gap-6 justify-center px-2">
          <!-- Back Button - Only show if AI generator is active -->
          <button
            v-if="showAIGenerator"
            @click="handleBackToCompletedSessions"
            class="w-12 h-12 sm:w-14 sm:h-14 md:w-15 md:h-15 bg-white border-2 sm:border-3 border-black rounded-full flex items-center justify-center cursor-pointer shadow-none text-black transition-all duration-300 ease-in-out hover:transform hover:translate-y-[-2px] hover:shadow-[0_4px_8px_rgba(0,0,0,0.1)] flex-shrink-0"
          >
            <svg
              class="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-black"
              fill="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                d="M11.03 3.97a.75.75 0 0 1 0 1.06l-6.22 6.22H21a.75.75 0 0 1 0 1.5H4.81l6.22 6.22a.75.75 0 1 1-1.06 1.06l-7.5-7.5a.75.75 0 0 1 0-1.06l7.5-7.5a.75.75 0 0 1 1.06 0Z"
                clip-rule="evenodd"
              />
            </svg>
          </button>

          <!-- Title Content -->
          <div class="text-center">
            <h1 class="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900">
              My Sessions
            </h1>
            <p class="text-sm sm:text-base md:text-lg text-black mt-2">
              Track your learning progress and generate AI-powered Study Materials.
            </p>
          </div>
        </div>
      </div>

      <!-- Summary Cards -->
      <div
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8"
      >
        <SummaryCard
          icon="calendar"
          title="Upcoming Sessions"
          :value="totalUpcomingSessions.toString()"
          :isLoading="isLoadingSessions"
          color="blue"
        />
        <SummaryCard
          icon="check"
          title="Completed Sessions"
          :value="totalCompletedSessions.toString()"
          :isLoading="isLoadingSessions"
          color="green"
        />
        <SummaryCard
          icon="star"
          title="Your Average Rating"
          :value="averageRating"
          :isLoading="isLoadingSessions"
          color="yellow"
          :isRating="true"
        />
        <SummaryCard
          icon="clock"
          title="Total Hours"
          :value="totalHours"
          :isLoading="isLoadingSessions"
          color="purple"
        />
      </div>

      <!-- Session Navigation Tabs -->
      <div ref="sessionNavTabs">
        <SessionNavigation
          :active-tab="activeTab"
          :tabs="navigationTabs"
          @tab-change="handleTabChange"
        />
      </div>

      <!-- AI Note Processor Section - Only show if user clicked "Generate AI Summary" from completed sessions -->
      <div v-if="showAIGenerator" ref="aiGeneratorSection" class="mb-6">
        <!-- Session Information Indicator -->
        <div
          v-if="sessionInfo"
          class="bg-white rounded-xl shadow-[6px_4px_0_0_rgba(0,0,0,1)] border-2 border-black mb-6 overflow-hidden"
        >
          <!-- Header Section -->
          <div class="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-8">
            <div class="flex items-center gap-4 mb-3">
              <div
                class="w-14 h-14 bg-white/60 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg border border-gray-200"
              >
                <svg
                  class="w-8 h-8 text-gray-700"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div class="flex-1">
                <h2 class="text-2xl font-bold text-gray-700 drop-shadow-sm">
                  Session Study Materials
                </h2>
                <p class="text-gray-600 text-sm mt-1">
                  🎯 Creating personalized AI study materials for this completed session
                </p>
              </div>
            </div>
          </div>

          <!-- Content Section -->
          <div class="p-8 bg-gradient-to-b from-gray-50 to-white">
            <!-- Tutor & Subject Row - Featured -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <!-- Tutor Card - Enhanced -->
              <div
                class="relative bg-white rounded-xl p-6 border-2 border-blue-300 shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div
                  class="absolute top-0 right-0 w-24 h-24 bg-blue-100 rounded-bl-full opacity-50"
                ></div>
                <div class="relative flex items-center gap-4">
                  <div
                    class="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center overflow-hidden shadow-lg ring-4 ring-blue-100"
                  >
                    <img
                      v-if="sessionInfo.tutorProfileImage"
                      :src="sessionInfo.tutorProfileImage"
                      :alt="sessionInfo.tutorName"
                      class="w-full h-full object-cover"
                    />
                    <svg
                      v-else
                      class="w-10 h-10 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </div>
                  <div>
                    <p class="text-xs text-blue-600 font-bold uppercase tracking-wide mb-1">
                      👨‍🏫 Your Tutor
                    </p>
                    <p class="text-lg font-bold text-gray-900">{{ sessionInfo.tutorName }}</p>
                  </div>
                </div>
              </div>

              <!-- Subject Card - Enhanced -->
              <div
                class="relative bg-white rounded-xl p-6 border-2 border-green-300 shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div
                  class="absolute top-0 right-0 w-24 h-24 bg-green-100 rounded-bl-full opacity-50"
                ></div>
                <div class="relative flex items-center gap-4">
                  <div
                    class="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center shadow-lg"
                  >
                    <svg
                      class="w-9 h-9 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                      />
                    </svg>
                  </div>
                  <div>
                    <p class="text-xs text-green-600 font-bold uppercase tracking-wide mb-1">
                      📚 Subject
                    </p>
                    <p class="text-lg font-bold text-gray-900">{{ sessionInfo.subject }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Session Details Grid -->
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <!-- Date Card -->
              <div
                class="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-5 border-2 border-purple-200 hover:border-purple-400 transition-colors duration-300"
              >
                <div class="flex items-start gap-3">
                  <div
                    class="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center shadow-md flex-shrink-0"
                  >
                    <svg
                      class="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-xs text-purple-700 font-bold uppercase tracking-wide mb-1">
                      📅 Date
                    </p>
                    <p class="text-sm font-semibold text-gray-900 break-words">
                      {{ sessionInfo.date }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Time Card -->
              <div
                class="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-5 border-2 border-orange-200 hover:border-orange-400 transition-colors duration-300"
              >
                <div class="flex items-start gap-3">
                  <div
                    class="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center shadow-md flex-shrink-0"
                  >
                    <svg
                      class="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-xs text-orange-700 font-bold uppercase tracking-wide mb-1">
                      ⏰ Time
                    </p>
                    <p class="text-sm font-semibold text-gray-900 break-words">
                      {{ sessionInfo.time }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Mode Card (if available) -->
              <div
                v-if="sessionInfo.mode"
                class="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-5 border-2 border-indigo-200 hover:border-indigo-400 transition-colors duration-300"
              >
                <div class="flex items-start gap-3">
                  <div
                    class="w-12 h-12 bg-indigo-500 rounded-lg flex items-center justify-center shadow-md flex-shrink-0"
                  >
                    <svg
                      class="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-xs text-indigo-700 font-bold uppercase tracking-wide mb-1">
                      💻 Mode
                    </p>
                    <p class="text-sm font-semibold text-gray-900">
                      {{ sessionInfo.mode === "face-to-face" ? "👥 Face-to-Face" : "🌐 Online" }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Location Card (if available) -->
              <div
                v-if="sessionInfo.location"
                class="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-5 border-2 border-teal-200 hover:border-teal-400 transition-colors duration-300 md:col-span-2 lg:col-span-3"
              >
                <div class="flex items-start gap-3">
                  <div
                    class="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center shadow-md flex-shrink-0"
                  >
                    <svg
                      class="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-xs text-teal-700 font-bold uppercase tracking-wide mb-1">
                      📍 Location
                    </p>
                    <p class="text-sm font-semibold text-gray-900 break-words">
                      {{ sessionInfo.location }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <AINoteProcessor
          :session-context="sessionContext"
          @material-generated="handleMaterialGenerated"
        />
      </div>

      <!-- AI-Generated Study Materials Section-->
      <AIStudyMaterialsGrid
        :hide-instructions="showAIGenerator"
        @view="handleViewMaterial"
        @export="handleExportMaterial"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, nextTick } from "vue";
import { useRouter, useRoute } from "vue-router";
import { db, getDoc, doc, auth } from "@/firebase";
import { collection, addDoc } from "firebase/firestore";
import StudentHeader from "@/components/StudentHeader.vue";
import StudentStatusBanner from "@/components/Student/StudentStatusBanner.vue";
import DocumentResubmissionAlert from "@/components/Student/DocumentResubmissionAlert.vue";
import SummaryCard from "@/components/Student-Tutor/SummaryCard.vue";
import AINoteProcessor from "@/components/Student/AINoteProcessor.vue";
import AIStudyMaterialsGrid from "@/components/Student/AIStudyMaterialsGrid.vue";
import SessionNavigation from "@/components/Student/StudentSessionNav.vue";
import { useSessionStore } from "@/composables/useSessionStore";
import FileReport from "@/components/FileReport.vue";

// Router for navigation
const router = useRouter();
const route = useRoute();

// Your Firebase user ID (keeping for backward compatibility with sessions)
const currentStudentId = ref("qb6ypxQovFZg9uzIyOTVlw2j35o1");

// Check if we should show the AI generator (from completed sessions)
const showAIGenerator = ref(false);
const sessionContext = ref<{
  sessionId: string;
  subject: string;
} | null>(null);

// Session information for display
interface SessionInfo {
  tutorName: string;
  tutorProfileImage?: string;
  subject: string;
  date: string;
  time: string;
  mode?: string;
  location?: string;
}

const sessionInfo = ref<SessionInfo | null>(null);

// Reference to AI generator section and session nav tabs for smooth scroll
const aiGeneratorSection = ref<HTMLElement | null>(null);
const sessionNavTabs = ref<HTMLElement | null>(null);

// Function to fetch session details from Firebase
const fetchSessionDetails = async (sessionId: string) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.error("No authenticated user");
      return;
    }

    // Fetch session document from Firestore
    const sessionDoc = await getDoc(doc(db, "sessions", sessionId));

    if (sessionDoc.exists()) {
      const sessionData = sessionDoc.data();

      // Fetch tutor information
      let tutorName = "Unknown Tutor";
      let tutorProfileImage = undefined;

      if (sessionData.tutorId) {
        const tutorDoc = await getDoc(doc(db, "users", sessionData.tutorId));
        if (tutorDoc.exists()) {
          const tutorData = tutorDoc.data();
          tutorName = `${tutorData.firstName || ""} ${tutorData.lastName || ""}`.trim();
          tutorProfileImage = tutorData.avatar || tutorData.profileImage;
        }
      }

      // Format date from startAt field
      let formattedDate = "N/A";
      if (sessionData.startAt) {
        const startDate = sessionData.startAt.toDate();
        formattedDate = startDate.toLocaleDateString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      }

      // Format time range from startAt and endAt fields
      let formattedTime = "N/A";
      if (sessionData.startAt && sessionData.endAt) {
        const startTime = sessionData.startAt.toDate();
        const endTime = sessionData.endAt.toDate();

        const startTimeStr = startTime.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });

        const endTimeStr = endTime.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });

        formattedTime = `${startTimeStr} - ${endTimeStr}`;
      }

      // Set session info
      sessionInfo.value = {
        tutorName,
        tutorProfileImage,
        subject: sessionData.subject || "Unknown Subject",
        date: formattedDate,
        time: formattedTime,
        mode: sessionData.mode,
        location: sessionData.location,
      };
    } else {
      console.error("Session not found");
    }
  } catch (error) {
    console.error("Error fetching session details:", error);
  }
};

onMounted(async () => {
  // Check if user came from "Generate AI Summary" button in completed sessions
  if (route.query.generateAI === "true" && route.query.sessionId && route.query.subject) {
    showAIGenerator.value = true;
    sessionContext.value = {
      sessionId: route.query.sessionId as string,
      subject: route.query.subject as string,
    };

    // Fetch session details
    await fetchSessionDetails(route.query.sessionId as string);
  }
});

// Watch for when AI generator is shown and scroll to top of page
watch(showAIGenerator, async (newValue) => {
  if (newValue) {
    await nextTick(); // Wait for DOM to update

    // Smooth scroll to the top of the page
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }
});

// Navigation state
const activeTab = ref("study-materials");
const isNavigating = ref(false);
const navigationTabs = ref([
  { id: "sessions", name: "Tutor Sessions" },
  { id: "session-history", name: "Session History" },
  { id: "study-materials", name: "AI-Generated Study Materials" },
]);

// Use shared session store (only for stats and completed sessions for AI generation)
const {
  completedSessions,
  isLoadingSessions,
  totalCompletedSessions,
  totalUpcomingSessions,
  averageRating,
  totalHours,
} = useSessionStore();

// Navigation handler
const handleTabChange = async (tabId: string) => {
  if (tabId === "sessions" || tabId === "session-history") {
    isNavigating.value = true;
    
    // Navigate to sessions with the specific tab (only add query if not default)
    if (tabId === "sessions") {
      await router.push("/student/sessions");
    } else {
      await router.push({
        path: "/student/sessions",
        query: { tab: tabId }
      });
    }
    
    isNavigating.value = false;
  } else {
    // Only update active tab if staying on current page
    activeTab.value = tabId;
  }
};

// Back button handler - navigate to sessions tab and scroll to completed sessions
const handleBackToCompletedSessions = async () => {
  showAIGenerator.value = false;
  sessionContext.value = null;

  // Navigate to sessions tab
  await router.push("/student/sessions");

  // Wait for navigation and DOM update
  await nextTick();

  // Smooth scroll down to find completed sessions section
  // The completed sessions are typically after the upcoming sessions
  setTimeout(() => {
    window.scrollTo({
      top: document.body.scrollHeight * 0.6, // Scroll to about 60% down the page
      behavior: "smooth",
    });
  }, 300); // Small delay to ensure page is loaded
};

// Event handlers for AI materials (now handled by AIStudyMaterialsGrid component itself)
const handleMaterialGenerated = async (materialData: any) => {
  try {
    await addDoc(collection(db, "study-materials"), {
      ...materialData,
      studentId: currentStudentId.value,
      createdAt: new Date(),
    });
    console.log("Study material saved to Firebase");
    // After generation, hide the AI generator and show only the grid
    showAIGenerator.value = false;
    sessionContext.value = null;
    // Clear query parameters
    router.replace({ path: "/student/study-materials", query: {} });
  } catch (error) {
    console.error("Error saving study material:", error);
  }
};

const handleViewMaterial = (material: any) => {
  console.log("Viewing material:", material);
  // Handle viewing the material (e.g., open modal, navigate to detail page)
};

const handleExportMaterial = (material: any) => {
  console.log("Exporting material:", material);
  // Handle exporting the material (e.g., download PDF, etc.)
};
</script>

<style scoped>
.student-page-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: url("/student-reg-bg.png") center top/100% auto no-repeat;
  background-attachment: fixed;
  position: relative;
  width: 100%;
}
</style>
